import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useRequestIntegration } from '@/lib/api/integrations'
import { useTranslation } from 'react-i18next'

const schema = z.object({
  integrationName: z.string().min(1, 'Integration name is required'),
  description: z.string().optional(),
  useCase: z.string().optional(),
})

type FormData = z.infer<typeof schema>

interface RequestIntegrationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  storeId: string
}

export function RequestIntegrationDialog({
  open,
  onOpenChange,
  storeId,
}: RequestIntegrationDialogProps) {
  const { t } = useTranslation('integrations')

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const requestMutation = useRequestIntegration()

  const onSubmit = async (data: FormData) => {
    try {
      // Trim and convert empty strings to undefined
      const payload = {
        integrationName: data.integrationName.trim(),
        description: data.description?.trim() || undefined,
        useCase: data.useCase?.trim() || undefined,
        storeId,
      }

      await requestMutation.mutateAsync(payload)

      reset()
      onOpenChange(false)
    } catch (error) {
      // Error is already handled by the useRequestIntegration hook
      console.error('Failed to send integration request:', error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('requestIntegration.title')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="integrationName">{t('requestIntegration.integrationName')}</Label>
            <Input
              id="integrationName"
              placeholder={t('requestIntegration.integrationNamePlaceholder')}
              {...register('integrationName')}
            />
            {errors.integrationName && (
              <p className="text-sm text-destructive">
                {errors.integrationName.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t('requestIntegration.description')}</Label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Textarea
                  id="description"
                  placeholder={t('requestIntegration.descriptionPlaceholder')}
                  rows={3}
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="useCase">{t('requestIntegration.useCase')}</Label>
            <Controller
              name="useCase"
              control={control}
              render={({ field }) => (
                <Textarea
                  id="useCase"
                  placeholder={t('requestIntegration.useCasePlaceholder')}
                  rows={3}
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('requestIntegration.cancel')}
            </Button>
            <Button type="submit" disabled={requestMutation.isPending}>
              {requestMutation.isPending ? t('requestIntegration.sending') : t('requestIntegration.sendRequest')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
